"use client";

import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useState } from "react";

import { builder } from "@/builders";
import { ProfileData } from "@/builders/types/profile";

import { TimeSpent } from "../../dashboard/time-spent";
import { useFormContext } from "./edit-profile-form-context";
import { UserDetails } from "./user-details";

import { APP } from "@idss/libraries";
import clsx from "clsx";

import { useCookies } from "next-client-cookies";

interface OverviewPanelProps extends ProfileData {
  setPanel: Dispatch<SetStateAction<string>>;
}

export function OverviewPanel({ setPanel, ...props }: OverviewPanelProps) {
  const [page, setPage] = useState(0);

  const cookies = useCookies();
  const isAdmin = cookies.get(APP.USER_TYPE) === "admin";

  const { data: badges } = useQuery({
    queryKey: builder.account.profile.badges.$get(),
    queryFn: builder.$use.account.profile.badges,
    select: ({ data }) => data,
  });

  const form = useFormContext();
  const badge = badges?.[page];

  return (
    <Stack p={32} flex={1}>
      <SimpleGrid
        spacing={32}
        cols={{
          base: 1,
          md: 2,
        }}
      >
        <Stack
          gap={0}
          className="border rounded-2xl border-primary-border-light"
          flex={1}
        >
          <Flex justify="space-between" align="center" px={24} py={16}>
            <h2 className="prose-lg/medium text-primary-text-body">
              Profile Details
            </h2>
            <Button
              hidden={!isAdmin}
              onClick={() => {
                form.setFieldValue("edit_profile", true);
                setPanel("settings");
              }}
            >
              Edit Profile
            </Button>
          </Flex>

          <Divider />

          <Stack gap={24} p={24}>
            <UserDetails title="First Name" detail={props?.first_name} />
            <UserDetails title="Last Name" detail={props?.last_name} />
            <UserDetails title="Rank" detail={props?.rank_name} />
            <UserDetails title="Company" detail={props?.organization} />
            <UserDetails title="Module" detail={props?.department_name} />
            <UserDetails title="Staff ID" detail={props?.staff_id} />
            <UserDetails title="Phone number" detail={props?.phone_number} />
          </Stack>
        </Stack>

        <Stack
          p={24}
          className="border rounded-2xl border-primary-border-light"
          flex={1}
        >
          <Flex justify="space-between">
            <h2 className="text-primary-text-body prose-lg/semi-bold">
              Badges
            </h2>

            <Text
              component="span"
              className="px-2 py-1 rounded-lg prose-base/semi-bold text-primary-button-normal"
            >
              {badge?.points}{" "}
              <Tooltip label="Action Point">
                <abbr title="Action Point">AP</abbr>
              </Tooltip>
            </Text>
          </Flex>

          <Stack className="self-center" gap={0} flex={1} align="center">
            <Box component="figure" h={250} p={16}>
              <Avatar src={badge?.badge_url} size="100%" />
            </Box>
            <Text className="prose-base/medium text-primary-text-body">
              {badge?.badge_name}
            </Text>
          </Stack>

          <Flex
            p={6}
            className="overflow-auto border rounded-b-2xl bg-primary-background-dark border-primary-border-light scrollbar-none"
            justify="center"
            align="center"
            gap={15}
          >
            {badges?.map((item, idx) => (
              <ActionIcon
                size={54}
                variant="transparent"
                key={item.id}
                onClick={() => {
                  setPage(idx);
                }}
              >
                <Avatar
                  src={item?.badge_url}
                  className={clsx("object-contain", {
                    grayscale: item.id !== badge?.id,
                  })}
                />
              </ActionIcon>
            ))}
          </Flex>
        </Stack>
      </SimpleGrid>

      <Stack>
        <Flex px={24} py={16} justify="space-between" align="center">
          <Title order={2} className="text-primary-text-body prose-lg/medium">
            Performance Chart
          </Title>

          {/* <Select
            pointer
            withCheckIcon={false}
            data={[
              "Filter",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ]}
            defaultValue="Filter"
            rightSection={
              <ArrowDown2 size="13" color="var(--primary-text-body)" />
            }
          /> */}
        </Flex>

        <TimeSpent />
      </Stack>
    </Stack>
  );
}
